import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import DataGrid, {
    Column,
    Editing,
    Export,
    FilterRow,
    Item,
    KeyboardNavigation,
    MasterDetail,
    Pager,
    Paging,
    RangeRule,
    RequiredRule,
    SearchPanel,
    Toolbar
} from 'devextreme-react/data-grid';
import ExportGrid from '../components/ExportGrid';
import { LoadingPanel } from '../components/LoadingPanel';
import { Button } from "devextreme-react";
import { ROUTE_WELCOME } from '../routes/Routes';
import usePageTitle from "../hooks/usePageTitle";
import CustomPreparedHeaderCell from '../components/CustomPreparedHeaderCell';
import { allowedPageSizes } from '../components/GlobalDataGridConfigurations';
import { logExport } from '../hooks/useAuth';
import { pageFormats } from '../components/GlobalDataGridConfigurations';
import "./ListViewConfig.scss";

const ListViewConfig = (
    {
        addRecordHandler,
        addRecordText,
        allowRowDeleting,
        allowRowEditing,
        allowRowUpdating,
        columns,
        dataSource,
        enablePagerSizing,
        enablePaging,
        exportEnabled,
        filterRow,
        headingTwo,
        keyExpr,
        isChild,
        loadingIndicatorMessage,
        loadingIndicatorState,
        loadingIndicatorLocationId,
        masterDetailComponent,
        masterDetailEnabled,
        onRowUpdating,
        permissions,
        showAddRecordButton,
        showSearchPanel,
        showToolBar,
        title
    }
) => {
    usePageTitle(title);
    const navigate = useNavigate();
    const { user } = useAuth;

    const handleExport = (e) => {
        logExport(user.email)
        ExportGrid(e, title, true, 12)
    };

    return (
        <div 
            className={ isChild ? "block__main-listView block__main-listView-child" : "block__main-listView block__main-listView-parent" }
            id={loadingIndicatorLocationId}
        >
            <div className="block__heading-one">
                <h1> { title } </h1>
            </div>
            <div className="block__heading-two">
                <h2> { headingTwo } </h2>
            </div>
            {
                loadingIndicatorState ? 
                <LoadingPanel 
                    location={{ of: loadingIndicatorLocationId }} 
                    message={loadingIndicatorMessage} 
                />
                :
                <div>
                    <DataGrid
                        columnAutoWidth={true}
                        dataSource={dataSource}
                        elementAttr={{
                            ariaLabel: `${title}-list-view`,
                            role: "table"
                        }}
                        keyExpr={keyExpr}
                        onCellPrepared={CustomPreparedHeaderCell}
                        onExporting={e => handleExport(e)}
                        onRowUpdating={onRowUpdating}
                        rowAlternationEnabled={true}
                        showBorders={true}
                        showColumnLines={true}
                        showRowLines={true}
                        visible={true}
                    >
                        <Editing
                            mode="row"
                            allowAdding={allowRowEditing}
                            allowDeleting={allowRowDeleting}
                            allowUpdating={allowRowUpdating}
                        />
                        <FilterRow visible={filterRow ?? true} />
                        <KeyboardNavigation enabled={true} />
                        <Paging
                            defaultPageIndex={0} 
                            defaultPageSize={10}
                            enabled={enablePaging ?? true}
                        />
                        <Pager
                            allowedPageSizes={allowedPageSizes}
                            showPageSizeSelector={true}
                            visible={enablePagerSizing ?? true}
                        />
                        <SearchPanel
                            highlightCaseSensitive={true}
                            visible={showSearchPanel ?? false}
                        />
                        {
                            showToolBar &&
                            <Toolbar>
                                <Item 
                                    location='before' 
                                    locateInMenu='auto' 
                                    visible={showAddRecordButton ?? false}
                                >
                                    <Button
                                        disabled={!permissions.allowCreate}
                                        elementAttr={{
                                            class: 'block__button'
                                        }}
                                        icon='plus'
                                        id="add_record_button"
                                        stylingMode="contained"
                                        onClick={addRecordHandler}
                                        text={addRecordText}
                                    />
                                </Item>
                                <Item 
                                    locateInMenu="auto" 
                                    name="exportButton" 
                                /> 
                                <Item
                                    locateInMenu="auto"
                                    name="searchPanel"
                                />
                                <Item location='after'>
                                    <Button 
                                        elementAttr={{ class: 'block__button' }}
                                        icon='home'
                                        stylingMode='outlined' 
                                        text='Home'
                                        type='normal' 
                                        onClick={() => navigate(ROUTE_WELCOME.withSlash)}
                                    />
                                </Item>
                            </Toolbar>
                        }
                        {
                            columns.map((
                                {
                                    allowEditing,
                                    calculateCellValue,
                                    calculateDisplayValue,
                                    caption,
                                    cellRender,
                                    dataField,
                                    dataType,
                                    format,
                                    requiredRuleMessage,
                                    rangeRuleMax,
                                    rangeRuleMessage,
                                    rangeRuleMin,
                                    rangeRuleReevaluate,
                                    type,
                                    typeButtonName,
                                    visible
                                }
                            ) => {
                                return (
                                    <Column 
                                        alignment='left'
                                        allowEditing={allowEditing}
                                        calculateCellValue={calculateCellValue}
                                        calculateDisplayValue={calculateDisplayValue}
                                        caption={caption}
                                        cellRender={cellRender}
                                        dataField={dataField}
                                        dataType={dataType}
                                        format={format}
                                        type={type}
                                        visible={visible}
                                    >
                                        {
                                            type === "buttons" &&
                                            <Button name={typeButtonName} />
                                        }
                                        {
                                            requiredRuleMessage &&
                                            <RequiredRule message={requiredRuleMessage} />
                                        }
                                        {
                                            rangeRuleMessage &&
                                            <RangeRule
                                                max={rangeRuleMax}
                                                min={rangeRuleMin} 
                                                message={rangeRuleMessage}
                                                reevaluate={rangeRuleReevaluate}
                                                type={"range"}
                                            />
                                        }
                                    </Column>
                                )
                            })
                        }
                        <Export 
                            enabled={exportEnabled ?? false}
                            formats={pageFormats}
                        />
                        <MasterDetail 
                            enabled={masterDetailEnabled ?? false}
                            component={masterDetailComponent}
                        />
                    </DataGrid>
                </div>
            }
        </div>
    );
};

export default ListViewConfig;